import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Download, Printer, Mail, ShieldCheck, FlaskConical, Stethoscope, CheckCircle2, Clock, Circle } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { useAsaAuth } from "../AuthContext";
import { loadDiary, diaryStats, predictFlare, anyWearableConnected, WEARABLE_DATA } from "../phase2";
import { symptomLabel, DOWNLOADS, gbp, FAMILY_DEGREES } from "../data";
import { startDownloadCheckout } from "../payments";
import { suggestedTestsFor, analyseMarkers } from "../clinicalRules";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function loadLastCheck() {
  try { return JSON.parse(localStorage.getItem("asa_last_check") || "null"); } catch { return null; }
}

function serverStats(entries) {
  const sorted = [...entries].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const sev = sorted.map((e) => Number(e.severity) || 0).filter(Boolean);
  const avg = sev.length ? Number((sev.reduce((a, b) => a + b, 0) / sev.length).toFixed(1)) : 0;
  const freq = {};
  sorted.forEach((e) => (e.symptoms || []).forEach((s) => { freq[s] = (freq[s] || 0) + 1; }));
  const top = Object.entries(freq).map(([id, count]) => ({ label: symptomLabel(id), count })).sort((a, b) => b.count - a.count);
  const half = Math.floor(sev.length / 2);
  const earlier = half ? sev.slice(0, half).reduce((a, b) => a + b, 0) / half : 0;
  const recent = half ? sev.slice(half).reduce((a, b) => a + b, 0) / (sev.length - half) : 0;
  const direction = !half ? "Not enough data" : recent < earlier - 0.3 ? "Improving" : recent > earlier + 0.3 ? "Worsening" : "Stable";
  return { count: sorted.length, avgSeverity: avg, flares: sorted.filter((e) => e.flare).length, recentFlares: sorted.slice(-7).filter((e) => e.flare).length, top, direction };
}

export default function GpReport() {
  const { user } = useAsaAuth();
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [serverDiary, setServerDiary] = useState(null);
  const [markers, setMarkers] = useState({});
  const [serverCheck, setServerCheck] = useState(null);
  const [checkLoaded, setCheckLoaded] = useState(false);
  const [tracker, setTracker] = useState({});

  useEffect(() => {
    axios.get(`${API}/diary`).then((r) => setServerDiary(r.data.entries || [])).catch(() => setServerDiary([]));
    axios.get(`${API}/markers`).then((r) => setMarkers(r.data.markers || {})).catch(() => {});
    axios.get(`${API}/checker/latest`).then((r) => setServerCheck(r.data.result || null)).catch(() => {}).finally(() => setCheckLoaded(true));
    axios.get(`${API}/test-tracker`).then((r) => setTracker(r.data.statuses || {})).catch(() => {});
  }, []);

  const diary = useMemo(() => loadDiary(), []);
  const stats = useMemo(() => (serverDiary && serverDiary.length ? serverStats(serverDiary) : diaryStats(diary)), [serverDiary, diary]);
  const connected = anyWearableConnected();
  const pred = useMemo(() => predictFlare(stats, WEARABLE_DATA, connected), [stats, connected]);
  // Trust server diary as the source of truth once it has loaded (ignore stale localStorage).
  const diaryCount = serverDiary !== null ? serverDiary.length : stats.count;
  const enoughForecastData = diaryCount >= 3 || connected;
  const forecastValue = enoughForecastData ? `${pred.risk}% (${pred.level})` : "Not enough data";
  const lastCheck = serverCheck || loadLastCheck();
  const markerNames = Object.keys(markers);
  const price = (DOWNLOADS.find((d) => d.id === "gp_report") || {}).price || 4.99;
  const today = new Date().toISOString().slice(0, 10);

  const clusters = (lastCheck?.results || []).filter((c) => c && c.name);
  const checkerSymptoms = (lastCheck?.symptoms || []);
  const familyDegreeLabel = (FAMILY_DEGREES.find((d) => d.id === lastCheck?.family_degree) || {}).label || "Not stated";
  const suggestedTests = useMemo(() => suggestedTestsFor(clusters.map((c) => c.name)), [lastCheck]);
  const flaggedMarkers = useMemo(() => analyseMarkers(markers), [markers]);
  const abnormalMarkers = flaggedMarkers.filter((m) => m.abnormal);

  const orderedCount = suggestedTests.filter((t) => tracker[t] === "ordered" || tracker[t] === "done").length;
  const doneCount = suggestedTests.filter((t) => tracker[t] === "done").length;

  const cycleTest = async (name) => {
    const current = tracker[name] || "pending";
    const next = current === "ordered" ? "done" : current === "done" ? "pending" : "ordered";
    const prev = tracker;
    const optimistic = { ...tracker };
    if (next === "pending") delete optimistic[name]; else optimistic[name] = next;
    setTracker(optimistic);
    try {
      const { data } = await axios.post(`${API}/test-tracker`, { name, status: next });
      setTracker(data.statuses || {});
    } catch {
      setTracker(prev);
      toast.error("Could not update. Please try again.");
    }
  };

  const buy = async () => {
    setLoading(true);
    try {
      await startDownloadCheckout({ downloadId: "gp_report", title: "GP / Doctor Report", email: user?.email, returnTo: "/asa/gp-report" });
    } catch (e) {
      setLoading(false);
      toast.error("Could not start checkout. Please try again.");
    }
  };

  const buildSummaryText = () => {
    const lines = [
      `ASAL HEALTH SUMMARY — ${user?.name || user?.email || "Patient"}`,
      `Generated: ${today}`,
      "",
      "PATIENT PROFILE",
      `- Diagnosis status: ${user?.diagnosed === "yes" ? "Already diagnosed" : user?.diagnosed === "no" ? "Awaiting diagnosis" : "Not stated"}`,
      ...(user?.diagnosed === "yes" ? [`- Condition(s): ${user?.diagnosis_conditions || "—"}`, `- Diagnosed since: ${user?.diagnosis_when || "—"}`] : []),
      `- Current medications: ${user?.medications || "—"}`,
      `- Recent tests (last 3 months): ${user?.recent_tests === "yes" ? (user?.recent_tests_detail || "Yes") : "None"}`,
      "",
      "SYMPTOM CHECKER OUTCOME",
      lastCheck ? "- Symptom awareness check: completed" : "- No awareness check yet",
      "",
      `SYMPTOMS RECORDED (awareness check) — ${checkerSymptoms.length} total`,
      checkerSymptoms.length ? checkerSymptoms.map((s) => `- ${symptomLabel(s)}`).join("\n") : "- No symptoms recorded in the awareness check yet",
      `- Family history: ${familyDegreeLabel}`,
      "",
      "SYMPTOM DIARY SUMMARY",
      `- Entries logged: ${stats.count}`,
      `- Average severity: ${stats.avgSeverity}/5`,
      `- Symptom episodes: ${stats.flares}`,
      `- Progress trend: ${stats.direction}`,
      `- Top symptoms: ${stats.top.slice(0, 5).map((s) => s.label).join(", ") || "none"}`,
      "",
      "SYMPTOM FORECAST",
      `- 7-day symptom-episode risk: ${forecastValue}`,
      "",
      "LATEST BLOOD / TEST RESULTS",
      markerNames.length ? markerNames.map((n) => { const s = markers[n]; const l = s[s.length - 1]; return `- ${n}: ${l.value}${l.unit || ""} (${l.date})`; }).join("\n") : "- None tagged",
      "",
      "CLINICAL ANALYSIS & SUGGESTED NEXT STEPS (educational — not a diagnosis or risk score)",
      "Conditions your recorded symptoms overlap with:",
      clusters.length ? clusters.map((c) => `- ${c.name}`).join("\n") : "- Symptom awareness check not completed yet",
      "Investigations to consider discussing with a clinician:",
      suggestedTests.map((t) => `- ${t}`).join("\n"),
      abnormalMarkers.length ? "Blood markers outside typical range:" : "Blood markers: none flagged outside typical range.",
      ...(abnormalMarkers.length ? abnormalMarkers.map((m) => `- ${m.name}: ${m.value}${m.unit} — ${m.status}${m.trend ? `, ${m.trend}` : ""} (${m.rangeStr})`) : []),
      "Note: The reporting lab's own reference range always takes precedence. This is information to discuss with your clinician, not a diagnosis.",
    ];
    return lines.join("\n");
  };

  const emailToGp = async () => {
    const to = window.prompt("Enter your GP's email address to send this summary:");
    if (!to) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) return toast.error("Please enter a valid email address");
    setEmailing(true);
    try {
      const { data } = await axios.post(`${API}/gp-report/email`, { to_email: to, body: buildSummaryText(), patient_name: user?.name });
      if (data.sent) toast.success(`Report emailed to ${to}`);
      else toast.message(`Saved — your report will be delivered to ${to} once email sending is enabled.`);
    } catch { toast.error("Could not send. Please try again."); }
    setEmailing(false);
  };

  const Row = ({ label, value }) => (
    <div className="flex justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-[13px] text-slate-500">{label}</span>
      <span className="text-[13px] font-medium text-slate-700 text-right max-w-[60%]">{value || "—"}</span>
    </div>
  );

  return (
    <AppLayout title="GP / Doctor Report">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <p className="text-slate-500 text-sm max-w-xl">A clinician-ready summary compiled from your checker results, symptom diary, wearables and symptom forecast. Preview it below, then download a formatted PDF.</p>
        <button onClick={buy} disabled={loading} className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] disabled:opacity-60 text-white font-semibold text-sm shrink-0 transition-colors">
          <Download size={17} /> {loading ? "Redirecting…" : `Download PDF — ${gbp(price)}`}
        </button>
      </div>

      {/* Report preview */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <div className="flex items-center justify-between border-b border-slate-200 pb-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-[#e6f4f2] text-[#0d9488] flex items-center justify-center"><FileText size={20} /></div>
            <div>
              <h2 className="font-head font-bold text-slate-800">ASAL — Health Summary</h2>
              <p className="text-[12px] text-slate-400">Generated {today}</p>
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 text-[12px] font-semibold text-[#0f766e] bg-[#e6f4f2] px-2.5 py-1 rounded-full"><ShieldCheck size={13} /> Confidential</span>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mt-5">
          <div>
            <h3 className="font-head font-semibold text-slate-800 mb-1">Patient</h3>
            <Row label="Name" value={user?.name} />
            <Row label="Email" value={user?.email} />
            <Row label="Member since" value={user?.since} />
            <Row label="Diagnosis status" value={user?.diagnosed === "yes" ? "Already diagnosed" : user?.diagnosed === "no" ? "Awaiting diagnosis" : "—"} />
            {user?.diagnosed === "yes" && <Row label="Condition(s)" value={user?.diagnosis_conditions} />}
            {user?.diagnosed === "yes" && <Row label="Diagnosed since" value={user?.diagnosis_when} />}
            <Row label="Current medications" value={user?.medications} />
            <Row label="Recent tests (3 mo)" value={user?.recent_tests === "yes" ? (user?.recent_tests_detail || "Yes") : user?.recent_tests === "no" ? "None" : "—"} />
          </div>
          <div>
            <h3 className="font-head font-semibold text-slate-800 mb-1">Awareness check summary</h3>
            <Row label="Symptom awareness check" value={lastCheck ? "Completed" : "Not completed yet"} />
            <Row label="Allergies" value={lastCheck?.allergies} />
            <Row label="Intolerances" value={lastCheck?.intolerances} />
            <Row label="Existing conditions" value={lastCheck?.existing} />
            <Row label="Medication" value={lastCheck?.medications} />
            <Row label="Recent illness (3 mo)" value={lastCheck?.recentIllness} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mt-2">
          <div>
            <h3 className="font-head font-semibold text-slate-800 mb-1">Symptom diary summary</h3>
            <Row label="Entries logged" value={String(stats.count)} />
            <Row label="Average severity" value={`${stats.avgSeverity}/5`} />
            <Row label="Episodes logged" value={String(stats.flares)} />
            <Row label="Top symptoms" value={stats.top.slice(0, 3).map((s) => s.label).join(", ")} />
            <Row label="Progress trend" value={stats.direction} />
          </div>
          <div>
            <h3 className="font-head font-semibold text-slate-800 mb-1">Symptom forecast & wearables</h3>
            <Row label="7-day symptom-episode risk" value={forecastValue} />
            <Row label="Wearable connected" value={connected ? "Yes" : "No"} />
            {connected && <Row label="Resting HR / HRV" value={`${WEARABLE_DATA.restingHR.today}bpm / ${WEARABLE_DATA.hrv.today}ms`} />}
            {connected && <Row label="Sleep (last night)" value={`${WEARABLE_DATA.sleep.today}h`} />}
          </div>
        </div>

        <div className="mt-4" data-testid="gp-report-all-symptoms">
          <h3 className="font-head font-semibold text-slate-800 mb-1">Symptoms recorded ({checkerSymptoms.length})</h3>
          {!checkLoaded && checkerSymptoms.length === 0 ? (
            <p className="text-[13px] text-slate-400 py-1">Loading your recorded symptoms…</p>
          ) : checkerSymptoms.length ? (
            <div className="flex flex-wrap gap-1.5 mt-1">
              {checkerSymptoms.map((s) => (
                <span key={s} className="text-[12.5px] bg-[#f4fbfa] border border-[#cfe9e5] text-slate-700 px-2.5 py-1 rounded-md">{symptomLabel(s)}</span>
              ))}
            </div>
          ) : (
            <p className="text-[13px] text-slate-400 py-1">No symptoms recorded in the Symptom Checker yet.</p>
          )}
          <p className="text-[13px] text-slate-600 mt-2"><span className="text-slate-400">Family history:</span> <span className="font-medium">{familyDegreeLabel}</span></p>
        </div>

        <div className="mt-4">
          <h3 className="font-head font-semibold text-slate-800 mb-1 flex items-center gap-1.5"><FlaskConical size={15} className="text-[#0d9488]" /> Latest blood / test results</h3>
          {markerNames.length === 0 ? (
            <p className="text-[13px] text-slate-400 py-2">No blood markers tagged yet. Add results in <span className="font-medium text-slate-600">My Records</span> to include them here.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-x-8">
              {markerNames.map((name) => {
                const series = markers[name];
                const latest = series[series.length - 1];
                const prev = series.length > 1 ? series[series.length - 2] : null;
                const arrow = prev ? (latest.value > prev.value ? "↑" : latest.value < prev.value ? "↓" : "→") : "";
                return <Row key={name} label={name} value={`${latest.value}${latest.unit || ""} ${arrow} ${prev ? `(was ${prev.value}${prev.unit || ""})` : ""} · ${latest.date}`} />;
              })}
            </div>
          )}
        </div>

        <div className="mt-6 rounded-xl border border-[#cfe9e5] bg-[#f4fbfa] p-4 sm:p-5" data-testid="gp-report-clinical-analysis">
          <h3 className="font-head font-semibold text-slate-800 mb-1 flex items-center gap-1.5"><Stethoscope size={16} className="text-[#0d9488]" /> Clinical analysis &amp; suggested next steps</h3>
          <p className="text-[12px] text-slate-500 mb-3">Rule-based decision support to discuss with a clinician — <strong>not a diagnosis</strong>.</p>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Conditions your symptoms overlap with</p>
              {clusters.length ? (
                <ul className="space-y-1.5" data-testid="gp-report-clusters">
                  {clusters.map((c) => (
                    <li key={c.name} className="flex items-center gap-2 text-[13px]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#0d9488] shrink-0" />
                      <span className="text-slate-700">{c.name}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[13px] text-slate-400">Run the Symptom Checker to populate this.</p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Investigations to consider</p>
                {suggestedTests.length > 0 && (
                  <span className="text-[11px] text-slate-500" data-testid="tracker-progress">{doneCount} done · {orderedCount} ordered · {suggestedTests.length} total</span>
                )}
              </div>
              <p className="text-[11px] text-slate-400 mb-2">Tap a test to track it: <span className="text-slate-500">Pending → Ordered → Done</span>.</p>
              <div className="flex flex-wrap gap-1.5" data-testid="gp-report-suggested-tests">
                {suggestedTests.map((t) => {
                  const s = tracker[t] || "pending";
                  const style = s === "done"
                    ? "bg-green-50 border-green-300 text-green-700"
                    : s === "ordered"
                    ? "bg-amber-50 border-amber-300 text-amber-700"
                    : "bg-white border-[#cfe9e5] text-slate-600 hover:border-[#0d9488]";
                  const Icon = s === "done" ? CheckCircle2 : s === "ordered" ? Clock : Circle;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => cycleTest(t)}
                      data-testid={`test-track-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      className={`inline-flex items-center gap-1.5 text-[12px] border px-2 py-1 rounded-md transition-colors ${style}`}
                    >
                      <Icon size={13} className={s === "pending" ? "text-slate-300" : ""} />
                      <span className={s === "done" ? "line-through" : ""}>{t}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Blood markers outside typical range</p>
            {abnormalMarkers.length ? (
              <div className="space-y-1.5" data-testid="gp-report-flagged-markers">
                {abnormalMarkers.map((m) => (
                  <div key={m.name} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px]">
                    <span className="font-medium text-slate-700">{m.name}:</span>
                    <span className="text-slate-700">{m.value}{m.unit}</span>
                    <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${m.status === "Above typical range" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{m.status}</span>
                    {m.trend && <span className="text-[11px] text-slate-400">{m.trend === "rising" ? "↑ rising" : m.trend === "falling" ? "↓ falling" : "→ stable"}</span>}
                    {m.rangeStr && <span className="text-[11px] text-slate-400">({m.rangeStr})</span>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[13px] text-slate-400">{markerNames.length ? "No tagged markers fall outside typical adult ranges." : "No blood markers tagged yet."}</p>
            )}
          </div>
          <p className="text-[11px] text-slate-400 mt-3">The reporting lab's own reference range always takes precedence over the typical ranges shown here.</p>
        </div>

        <p className="text-[12px] text-slate-400 mt-6 pt-4 border-t border-slate-100" data-testid="gp-report-disclaimer">This summary is generated by ASAL for information only and is not a medical diagnosis. © ASA Foundation · Copyright &amp; IP: Asal Shirazi.</p>
      </div>

      <div className="flex flex-wrap gap-3 mt-5">
        <button onClick={buy} disabled={loading} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] disabled:opacity-60 text-white font-semibold text-sm"><Download size={16} /> {loading ? "Redirecting…" : `Download PDF — ${gbp(price)}`}</button>
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50"><Printer size={16} /> Print</button>
        <button data-testid="email-gp-btn" onClick={emailToGp} disabled={emailing} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-[#0d9488] text-[#0f766e] font-semibold text-sm hover:bg-[#e6f4f2] disabled:opacity-60"><Mail size={16} /> {emailing ? "Sending…" : "Email to my GP"}</button>
      </div>
      <p className="text-[12px] text-slate-400 flex items-center gap-1.5 mt-4"><ShieldCheck size={13} /> This report is generated from data stored securely in your ASAL account.</p>
    </AppLayout>
  );
}
